/**
 * Findings service
 */

import { apiClient } from '../utils/api';
import type { ComplianceFinding, ApiResponse } from '../../../shared/src/types';

export interface CreateFindingData {
  installation_id: string;
  checklist_run_id: string;
  checklist_item_run_id?: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  description: string;
  recommendation?: string;
}

export interface ResolveFindingData {
  resolution_notes: string;
}

export async function createFinding(data: CreateFindingData): Promise<ComplianceFinding> {
  const response = await apiClient.post<ApiResponse<ComplianceFinding>>('/findings', data);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error creating finding');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

export async function listFindings(
  installationId?: string,
  checklistRunId?: string,
  status?: 'open' | 'resolved' | 'dismissed'
): Promise<ComplianceFinding[]> {
  const queryParams = new URLSearchParams();
  if (installationId) queryParams.append('installation_id', installationId);
  if (checklistRunId) queryParams.append('checklist_run_id', checklistRunId);
  if (status) queryParams.append('status', status);

  const url = `/findings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await apiClient.get<ApiResponse<ComplianceFinding[]>>(url);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching findings');
  }

  return response.data.data || [];
}

export async function getFinding(id: string): Promise<ComplianceFinding> {
  const response = await apiClient.get<ApiResponse<ComplianceFinding>>(`/findings/${id}`);

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error fetching finding');
  }

  if (!response.data.data) {
    throw new Error('Finding not found');
  }

  return response.data.data;
}

export async function resolveFinding(id: string, data: ResolveFindingData): Promise<ComplianceFinding> {
  const response = await apiClient.post<ApiResponse<ComplianceFinding>>(
    `/findings/${id}/resolve`,
    data
  );

  if (response.data.error) {
    throw new Error(response.data.error.message || 'Error resolving finding');
  }

  if (!response.data.data) {
    throw new Error('Invalid response from server');
  }

  return response.data.data;
}

